import { Component, NgZone, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';

import { AuthService, StateService } from '../../services';
import { IRegistrationSimpleCredentials } from 'src/app/services/interfaces';
import { NgHcaptchaService } from 'ng-hcaptcha';

@Component({
  selector: 'app-registration-simple',
  templateUrl: './registration-simple.component.html',
  styleUrls: ['./registration-simple.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RegistrationSimpleComponent implements OnInit {
  public lang: string = null;
  public formCredentials: IRegistrationSimpleCredentials = {
    name: null,
    surname: null,
    mail: null,
    companyName: null,
    idLang: null,
    password: null,
    confirmPassword: null,
    hcaptchaResponse: null
  };
  public apiErrorMessage: string = null;
  public allFieldsRequired: boolean = false;
  public completeCaptcha: boolean = false;
  public passwordsNotEqual: boolean = false;
  public passwordLengthFail: boolean = true;
  public passwordLetterOrNumberFail: boolean = true;
  public passwordSymbolFail: boolean = true;
  public registrationSuccessful: boolean = false;
  public emailNotValid: boolean = null;
  public emailRegexp: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/gm;
  pendingForm: NgForm | null = null;

  constructor(
    private authService: AuthService,
    private stateService: StateService,
    private router: Router,
    private hcaptchaService: NgHcaptchaService,
    private ngZone: NgZone
  ) {
    // empty
  };

  public ngOnInit(): void {
    this.lang = this.stateService.restore('deviceLang');
  };
  public goToZilex() {
    window.open('https://zilex.de', '_blank');
  }
  /**
   * This method need to prevent trigger cancel button when enter `enter key`.
   * @param event
   */
  public preventEnterKeyTigger(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }
  /** Method to select new language, when user change it in menu. */
  public vmOnLanguageChange(value: string) {
    this.lang = this.stateService.restore('deviceLang');
  };


  /**
   * Method to submit registration form.
   * @param form
   */
  public submit(form: NgForm) {

    this.resetFlags();

    this.pendingForm = form;

    // Get language selected on device.
    // Use English as fallback if deviceLang will be empty
    this.formCredentials.idLang = this.stateService.restore('deviceLang') || 'en';

    if (this.formCredentials.mail) {
      if (this.formCredentials.mail.match(this.emailRegexp)) {
        this.emailNotValid = false;
        form.form.controls['mail'].setErrors(null);
      } else {
        this.emailNotValid = true;
        form.form.controls['mail'].setErrors({ 'incorrect': true });

      }
    }

    if (!this.allFormFieldsFilled()) {
      this.allFieldsRequired = true;
    }

    if (this.formCredentials.password && this.formCredentials.confirmPassword) {
      this.formCredentials.password = this.formCredentials.password.trim()
      this.formCredentials.confirmPassword = this.formCredentials.confirmPassword.trim();

      this.passwordsNotEqual = this.formCredentials.password !== this.formCredentials.confirmPassword;

      if (this.passwordsNotEqual) {
        form.form.controls['password'].setErrors({ 'incorrect': true });
        form.form.controls['confirmPassword'].setErrors({ 'incorrect': true });
      } else {
        form.form.controls['password'].setErrors(null);
        form.form.controls['confirmPassword'].setErrors(null);
      }

    }

    if (this.formCredentials.password) {
      this.formCredentials.password = this.formCredentials.password.trim()

      if (this.formCredentials.password.length < 6) {
        this.passwordLengthFail = true;
      }
      if (!(/[A-Za-z0-9]/.test(this.formCredentials.password))) {
        this.passwordLetterOrNumberFail = true;
      }
      if (!(/[^A-Za-z0-9]/.test(this.formCredentials.password))) {
        this.passwordSymbolFail = true;
      }
      if (this.formCredentials.password.length < 6 || !(/[A-Za-z0-9]/.test(this.formCredentials.password)) || !(/[^A-Za-z0-9]/.test(this.formCredentials.password))) {
        form.form.controls['password'].setErrors({ 'incorrect': true });
      } else {
        form.form.controls['password'].setErrors(null);
      }
    } else {
      this.passwordLengthFail = true;
      form.form.controls['password'].setErrors({ 'incorrect': true });
    }

    if (form.valid) {
      this.hcaptchaService.verify().subscribe((hcaptchaResponse: string) => {
        this.ngZone.run(() => this.onCaptchaResolved(hcaptchaResponse));
      });
    }

  };

  /**
   * Method to navigate to login page, by click on `cancel button`.
   */
  public cancel(e: PointerEvent): void {
    e.preventDefault();
    this.router.navigate(["/login"]);
  };

  private resetFlags(): void {
    this.passwordsNotEqual = false;
    this.passwordLengthFail = false;
    this.passwordLetterOrNumberFail = false;
    this.passwordSymbolFail = false;
    this.allFieldsRequired = false;
    this.completeCaptcha = false;
    this.emailNotValid = null;
  };

  /**
   * Method to check that all fields have values.
   */
  private allFormFieldsFilled(): boolean {

    const relevantFields = Object.entries(this.formCredentials)
      .filter(([key, _]) => key !== 'hcaptchaResponse')
      .map(([_, value]) => value);

    return relevantFields.every(item => item !== null && item !== undefined && item);

  };

  private onCaptchaResolved(hcaptchaResponse: string): void {
    this.formCredentials.hcaptchaResponse = hcaptchaResponse;

    if (this.pendingForm && this.pendingForm.valid) {
      const payload = Object.assign({}, this.formCredentials);


      this.authService.registrationSimple(payload).subscribe({
        next: () => {
  
          this.registrationSuccessful = true;
        },
        error: (error) => {
          if (typeof error.error === "object") {
            let message = '';
            Object.keys(error.error.errors).forEach((_key: string) => {

              error.error.errors[_key].forEach((_str) => {
                message = `${message} ${_str}`;
              })

            });
            this.apiErrorMessage = message;

          } else {
            this.apiErrorMessage = error.error;
          }

        }
      });
    }
  }

  public validate(): void {
    this.formCredentials.password = this.formCredentials.password.trim()
    if (this.formCredentials.password) {
      if (this.formCredentials.password.length < 6) {
        this.passwordLengthFail = true;
      } else {
        this.passwordLengthFail = false;
      }
      if (!(/[A-Za-z0-9]/.test(this.formCredentials.password))) {
        this.passwordLetterOrNumberFail = true;
      } else {
        this.passwordLetterOrNumberFail = false;
      }
      if (!(/[^A-Za-z0-9]/.test(this.formCredentials.password))) {
        this.passwordSymbolFail = true;
      } else {
        this.passwordSymbolFail = false;
      }
    } else {
      this.passwordLengthFail = true;
      this.passwordSymbolFail = true;
      this.passwordLetterOrNumberFail = true;
    }
  }

};

